<template>
    <div class="register d-flex flex-column fill-height">
        <Navigation />
        <v-main class="grow">
            <v-container fluid class="fill-height align-start pb-0">
                <v-row justify="center" class="fill-height">
                    <v-col cols="10">
                        <!-- Payment Form Card -->
                        <v-card v-if="showPaymentCard" :style="isThemeAja ? { backgroundColor: '#1E3945' } : {}">
                            <CardTitle :text="pageTitle" />

                            <v-card-text>
                                <v-form v-on:submit.prevent="onSubmit" ref="form">
                                    <!-- Resubscription Alert -->
                                    <v-row v-if="showResubscriptionAlert" justify="space-between" align="center" dense>
                                        <v-col cols="12">
                                            <v-alert color="warning" text dense prominent outlined>
                                                Votre adhésion arrive à échéance. Renouvelez-la pour continuer à accéder à tous les services.<br/>
                                                Votre nouvelle adhésion prendra effet au terme de votre la précédente.
                                            </v-alert>
                                        </v-col>
                                    </v-row>
                                    
                                    <!-- Subscription Section -->
                                    <v-row v-if="showSubscriptionSection" justify="space-between" align="center" dense>
                                        <v-col cols="12">
                                            <p v-if="$acl.check('isUser')" class="mt-3 mb-0 subtitle-1">
                                                Profitez de toutes les fonctionnalités de l'outil en ligne OncoGite ! 
                                                Inscription en ligne aux ateliers, suivi et rappel de vos séances,...
                                            </p>
                                            <p v-if="$acl.check('isPro')" class="mt-3 mb-0 subtitle-1">
                                                Vous pourrez ensuite {{ isResubscription ? 'continuer de' : '' }} créer vos ateliers, les séances et gérer vos patients bénéficiares,...
                                            </p>
                                        </v-col>
                                        <v-col cols="auto">
                                            <v-switch
                                                :disabled="isAja || isLigue || isPatientsreseau || isEmployer || isRetreat || isHealthinsurance || paymentType === 'subscription' || paymentType === 'resubscription'"
                                                v-model="isSubscriptionEnabled"
                                                label="Adhésion"
                                                class="font-weight-bold"
                                                :color="isThemeAja ? '#4E983C' : 'primary'"
                                            ></v-switch>
                                        </v-col>
                                        <v-col cols="auto">
                                            <v-text-field
                                                v-if="showSubscriptionAmount"
                                                v-model="subscription"
                                                label="Montant de l'adhésion"
                                                outlined
                                                disabled
                                                append-icon="euro"
                                                hide-details
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>
                                    
                                    <!-- Free Subscription Alert -->
                                    <v-row v-if="showDiscountAlert" justify="space-between" align="center" dense>
                                        <v-col cols="12">
                                            <v-alert :color="isThemeAja ? '#4E983C' : 'primary'" text dense prominent outlined icon="thumb_up">
                                                <b>Vous bénéficiez de l'adhésion à titre gracieux.</b>
                                            </v-alert>
                                        </v-col>
                                    </v-row>
                                    
                                    <!-- Donation Section -->
                                    <v-row v-if="showDonationSection" justify="space-between" align="center" dense>
                                        <hr style="width: 100%" />
                                        <v-col cols="12">
                                            <p class="mt-3 mb-0 subtitle-1">
                                                Notre association est reconnue d'intérêt général. Chaque don donne lieu à une réduction d'impôts.<br />
                                                Pour les particuliers : pour 100€ versés à <oncogite /> … votre don ne vous coûte que 34€ !
                                            </p>
                                        </v-col>
                                        <v-col cols="auto">
                                            <v-switch
                                                :color="isThemeAja ? '#4E983C' : 'primary'"
                                                v-model="isDonationEnabled"
                                                label="Don"
                                                class="font-weight-bold"
                                            ></v-switch>
                                        </v-col>
                                        <v-col cols="auto">
                                            <v-text-field
                                                :color="isThemeAja ? '#4E983C' : 'primary'"
                                                v-if="isDonationEnabled"
                                                v-model="donation"
                                                label="Montant du don"
                                                outlined
                                                append-icon="euro"
                                                hide-details
                                                type="number"
                                                min="0"
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>
                                    
                                    <!-- Terms and Conditions Section -->
                                    <v-row v-if="!isConfirmation" align="center">
                                        <hr style="width: 100%" />
                                        <v-col v-if="isSubscriptionEnabled" cols="12">
                                            <PlatformTerms 
                                                v-model="platform_accepted_terms"
                                                :isAja="isThemeAja"
                                            />
                                        </v-col>
                                        
                                        <v-checkbox
                                            v-if="isSubscriptionEnabled && !$acl.check('isPro') && !isLigue && !isAja && !isPatientsreseau"
                                            v-model="followed_cancerous_pathology"
                                            class="ml-4 mt-n4"
                                        >
                                            <template v-slot:label>
                                                <div>Je certifie que j'ai été ou suis suivi.e pour une pathologie cancéreuse*</div>
                                            </template>
                                        </v-checkbox>
                                        
                                        <!-- Total Amount -->
                                        <v-col class="text-right pb-5 mb-5 subtitle-1">
                                            <b>Total:</b>
                                        </v-col>
                                        <v-col cols="auto">
                                            <v-text-field
                                                class="mt-6"
                                                :value="total"
                                                type="number"
                                                label="Montant total"
                                                outlined
                                                disabled
                                                append-icon="euro"
                                            ></v-text-field>
                                        </v-col>
                                        
                                        <!-- Modify Reason Button -->
                                        <v-col cols="12">
                                            <v-btn 
                                                :color="isThemeAja ? 'white' : 'primary'" 
                                                text
                                                v-if="isSubscriptionEnabled && !$acl.check('isPro')"
                                                style="margin-top: -120px; margin-left: -10px" 
                                                depressed
                                                @click="modifyReason()"
                                            >
                                                Modifier mon orientation
                                            </v-btn>
                                        </v-col>
                                        
                                        <v-col v-if="total > 0" cols="12" class="text-center">
                                            Une fois votre choix validé, vous serez redirigé vers une page de paiement sécurisée.
                                        </v-col>
                                    </v-row>
                                    
                                    <!-- Submit Button with Tooltip -->
                                    <v-tooltip
                                        :disabled="isDonation || ((isSubscriptionEnabled && (followed_cancerous_pathology && allPlatformTermsAccepted) || ($acl.check('isPro') && allPlatformTermsAccepted)))"
                                        top
                                    >
                                        <template v-slot:activator="{ on }">
                                            <div v-on="on">
                                                <v-btn 
                                                    v-on="on" 
                                                    type="submit"
                                                    block
                                                    v-if="!isConfirmation"
                                                    :color="isThemeAja ? '#4E983C' : 'primary'" 
                                                    :loading="loading"
                                                    :disabled="isSubmitDisabled"
                                                >
                                                    Valider
                                                </v-btn>
                                            </div>
                                        </template>
                                        <span>Vous devez accepter les conditions ci-dessus avant de pouvoir continuer.</span>
                                    </v-tooltip>
                                </v-form>

                                <!-- Hidden form for eTransactions -->
                                <form ref="etransactionsForm" method="POST" :action="eTransactions.url">
                                    <input 
                                        v-for="(data, i) in eTransactions.datas" 
                                        :key="i"
                                        type="hidden"
                                        :name="data.key"
                                        :value="data.value"
                                    >
                                    <input type="hidden" name="PBX_HMAC" :value="eTransactions.hmac">
                                </form>
                            </v-card-text>
                        </v-card>
                        
                        <!-- Bravo Card (Subscription Success) -->
                        <v-dialog v-model="showBravoCard" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
                            <SuccessCard 
                                v-if="showBravoCard" 
                                title="Bravo"
                                :userType="$acl.check('isPro') ? 'pro' : 'user'"
                                cardType="bravo"
                                footerImage="bandeau_bravo.png"
                            />
                        </v-dialog>
                        
                        <!-- Thanks Card (Donation Success) -->
                        <SuccessCard 
                            v-if="showThanksCard" 
                            title="Merci !"
                            userType="user"
                            cardType="thanks"
                            footerImage="bandeau_merci.png"
                        />
                        
                        <!-- Payment Error Card -->
                        <SuccessCard 
                            v-if="isConfirmation && paymentError" 
                            title="Erreur"
                            cardType="default"
                        >
                            <template v-if="paymentError">
                                <v-alert type="error" text prominent outlined>
                                    Une erreur est survenue lors de votre paiement.
                                </v-alert>
                                <v-btn color="primary" class="mt-6 mr-4" @click="retryPayment">
                                    Réessayer
                                </v-btn>
                                <v-btn color="primary" class="mt-6" :to="{ name: 'home' }">
                                    Retourner à l'accueil
                                </v-btn>
                            </template>
                        </SuccessCard>
                    </v-col>
                </v-row>
            </v-container>
        </v-main>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import PlatformTerms from '@/components/payment/PlatformTerms.vue'
import SuccessCard from '@/components/payment/SuccessCard.vue'

export default {
    components: {
        PlatformTerms,
        SuccessCard
    },
    data() {
        return {
            // UI state
            loading: false,
            isConfirmation: false,
            showPaymentCard: true,
            showBravoCard: false,
            showThanksCard: false,
            showDiscountAlert: false,
            isFirstLoad: true,
            
            // Payment data
            subscription: null,
            donation: 30,
            discounted: 0,
            isDonation: false,
            eTransactions: [],
            
            // User data
            paymentContactMail: '',
            followed_cancerous_pathology: false,
            platform_accepted_terms: { 
                access: false, 
                secret: false, 
                discreet: false, 
                sceenshots: false 
            },
            partners: [],
            registration_process: null
        }
    },
    computed: {
        ...mapGetters(['isThemeAja', 'getProfile']),
        
        // ========== PAYMENT TYPE FLAGS ==========
        paymentType() {
            return this.$route.meta?.type || 'donation'
        },
        isSubscription() {
            return this.paymentType === 'subscription'
        },
        isSubscriptionEnabled: {
            get() {
                // Always enabled for subscription and resubscription routes
                return this.paymentType === 'subscription' || this.paymentType === 'resubscription'
            },
            set(value) {
                // Only allow toggling if on donation route
                if (this.paymentType === 'donation') {
                    this.isSubscription = value
                }
            }
        },
        isDonationEnabled: {
            get() {
                return this.isDonationType || this.isDonation
            },
            set(value) {
                this.isDonation = value
                this.donation = value ? 30 : 0
            }
        },
        isResubscription() {
            return this.paymentType === 'resubscription' && this.canResubscribe
        },
        isDonationType() {
            return this.paymentType === 'donation'
        },
        
        // ========== ELIGIBILITY FLAGS ==========
        canResubscribe() {
            if (!this.$acl.check('isPro') || !this.getProfile.premium_dates) return false
            
            const adhesionEndDate = new Date(this.getProfile.premium_dates['end'])
            const fifteenDaysFromNow = new Date()
            fifteenDaysFromNow.setDate(fifteenDaysFromNow.getDate() + 15)
            
            return adhesionEndDate <= fifteenDaysFromNow
        },

        // ========== USER TYPE FLAGS ==========
        isLigue() {
            return this.registration_process?.reason === 'ligue'
        },
        isPatientsreseau() {
            return this.registration_process?.reason === 'patientsreseau'
        },
        isEmployer() {
            return this.registration_process?.reason === 'employer'
        },
        isHealthinsurance() {
            return this.registration_process?.reason === 'healthinsurance'
        },
        isRetreat() {
            return this.registration_process?.reason === 'retreat'
        },
        isAja() {
            return this.registration_process?.reason === 'aja'
        },
        isAjaYoung() {
            return this.isAja && this.registration_process?.aja_role === 'aja_young'
        },
        isAjaAssistant() {
            return this.isAja && this.registration_process?.aja_role === 'aja_assistant'
        },

        // ========== UI DISPLAY FLAGS ==========
        pageTitle() {
            if (this.isConfirmation) return "Confirmation de votre paiement"
            if (this.isResubscription) return "Renouvellement de mon adhésion"
            if (this.isSubscription) return "Je finalise mon adhésion"
            return "Faire un don"
        },
        showDonationSection() {
            return !this.isConfirmation && !this.isAjaYoung
        },
        showSubscriptionSection() {
            return (this.isSubscription || this.isResubscription) && !this.isConfirmation
        },
        showSubscriptionAmount() {
            return this.isSubscriptionEnabled
        },
        showResubscriptionAlert() {
            return this.isResubscription && !this.isConfirmation
        },

        // ========== CALCULATIONS ==========
        total() {
            const subscriptionAmount = this.isSubscriptionEnabled ? parseFloat(this.subscription || 0) : 0
            const donationAmount = this.isDonationEnabled ? parseFloat(this.donation || 0) : 0
            return subscriptionAmount + donationAmount
        },

        // ========== VALIDATION ==========
        allPlatformTermsAccepted() {
            return Object.values(this.platform_accepted_terms).every(term => term === true)
        },
        isSubmitDisabled() {
            // Disable if total is less than 1 euro and discount alert is not shown
            if (!this.showDiscountAlert && this.total < 1) return true

            // Validation for subscription
            if (this.isSubscriptionEnabled) {
                return (!this.followed_cancerous_pathology && !this.$acl.check('isPro')) || 
                       !this.allPlatformTermsAccepted
            }

            return false
        },
        
        // ========== ROUTING & ERRORS ==========
        currentRoute() {
            if (this.isResubscription) return 'payment-resubscription'
            if (this.isSubscription) return 'payment-subscription'
            return 'payment-donation'
        },
        paymentError() {
            return this.$route.query.code !== '00000'
        }
    },
    watch: {
        '$route': {
            immediate: true,
            handler(to) {
                this.handleRouteChange(to)
            }
        },
        
        donation(val) {
            if (!val) this.donation = 0
        }
    },
    async mounted() {
        this.registration_process = this.$store.getters.getRegistrationProcess

        // Block AJA Assistants from resubscribing (they already used their free subscription)
        // AJA Assistants cannot resubscribe at all - they get one free subscription only
        const isAjaAssistant = this.registration_process?.reason === 'aja' &&
                               this.registration_process?.aja_role === 'aja_assistant'
        const hasAlreadyUsedGracious = this.getProfile?.has_used_gracious_subscription

        if (isAjaAssistant && hasAlreadyUsedGracious && this.paymentType === 'subscription') {
            await this.$store.dispatch('setErrors', { message: 'En tant qu\'aidant, vous ne pouvez pas réadhérer.' })
            this.$router.push({ name: 'home' })
            return
        }

        // Handle payment response if code exists in URL
        if (this.$route.query.code) {
            await this.handlePaymentResponse()
            return
        }

        // Initialize data - only fetch settings once
        await this.initData()

        // Initialize donation when on donation route
        if (this.isDonationType) {
            this.isDonation = true
            this.donation = this.$route.query.amount ? parseFloat(this.$route.query.amount) : 30
        }
    },
    methods: {
        // ========== INITIALIZATION METHODS ==========
        async initData() {
            // Fetch these in parallel for better performance
            await Promise.all([
                this.fetchPaymentContactMail(),
                this.getPartners()
            ])
            
            this.checkFollowedCancerousPathology()
            
            if (this.isSubscription || this.isResubscription) {
                this.handleSubscription()
            }
        },
        
        /**
         * Checks if the user is followed for cancerous pathology
         * Auto-sets to true for users from Ligue, AJA or Patients en réseau
         */
        checkFollowedCancerousPathology() {
            if (!this.$acl.check('isPro')) {
                this.followed_cancerous_pathology = this.isLigue || this.isAja || this.isPatientsreseau
            }
        },
        
        /**
         * Handles subscription initialization based on user type
         * Ligue, PER, AJA users get free subscription only for first adhesion
         * For resubscription, they must pay like regular users
         */
        handleSubscription() {
            if (this.isSubscription || this.isResubscription) {
                const hasPartnerPrice = this.registration_process?.partner?.id
                const isGraciousEligible = this.isAja || this.isLigue || this.isPatientsreseau
                const hasAlreadyUsedGracious = this.getProfile?.has_used_gracious_subscription

                if (hasPartnerPrice && !isGraciousEligible) {
                    // Partner-based subscription (employer, healthinsurance, etc.)
                    this.fetchPartnerAmount()
                } else if (isGraciousEligible && !hasAlreadyUsedGracious) {
                    // First subscription for Ligue/PER/AJA: free
                    this.setFreeSubscription()
                } else {
                    // Resubscription or standard user: paid subscription
                    this.fetchSubscriptionAmount()
                }
            }
        },
        
        /**
         * Handles route change by updating payment type and amounts
         */
        async handleRouteChange(route) {
            // Skip initialization for first load since mounted() will handle it
            if (this.isFirstLoad) {
                this.isFirstLoad = false
                return
            }
            
            // Reset states based on route type
            const routeType = route.meta?.type || 'donation'
            
            if (routeType === 'donation') {
                this.isDonation = true
                this.donation = route.query.amount ? parseFloat(route.query.amount) : 30
            } else {
                this.isDonation = false
                this.donation = 0
            }

            // Handle subscription initialization
            if ((routeType === 'subscription' || routeType === 'resubscription') && !this.isAja) {
                await this.fetchSubscriptionAmount()
            }
        },

        // ========== DATA FETCHING METHODS ==========
        /**
         * Fetches the subscription amount based on user type
         */
        fetchSubscriptionAmount() {
            let setting = 'subscription_amount'
            if (this.$acl.check('isPro')) {
                setting = 'pro_' + setting
            }

            this.$store.dispatch('settingRequest', { key: setting })
                .then((value) => {
                    this.subscription = parseFloat(value)
                })
                .catch((err) => {
                    if (err.response?.status !== 403) {
                        this.$store.dispatch('setErrors', err.response?.data)
                    }
                })
        },
        
        /**
         * Fetches partner pricing information
         */
        fetchPartnerAmount() {
            this.$store.dispatch('partnerRequest', { id: this.registration_process.partner.id })
                .then(result => {
                    this.discounted = 1
                    const partnerPrice = parseFloat(result.data.price)
                    
                    if (partnerPrice === 0) {
                        this.subscription = partnerPrice
                        this.showDiscountAlert = true
                    } else if (partnerPrice > 0) {
                        this.subscription = partnerPrice
                    }
                })
                .catch((err) => {
                    if (err.response?.status !== 403) {
                        this.$store.dispatch('setErrors', err.response?.data)
                    }
                })
        },
        
        /**
         * Fetches all partners
         */
        getPartners() {
            return this.$store.dispatch('partnersRequest', {})
                .then((result) => this.partners = result.data)
                .catch(() => this.$store.dispatch('setErrors', 'Error while fetching partners'))
        },
        
        /**
         * Fetches payment contact email from settings
         */
        fetchPaymentContactMail() {
            return this.$store.dispatch('settingRequest', { key: 'payment_contact_mail' })
                .then((value) => {
                    this.paymentContactMail = value
                })
                .catch((err) => {
                    if (err.response?.status !== 403) {
                        this.$store.dispatch('setErrors', err.response?.data)
                    }
                })
        },
        
        /**
         * Sets the subscription as free (0€)
         */
        setFreeSubscription() {
            this.subscription = 0
            this.showDiscountAlert = true
        },

        // ========== ACTION METHODS ==========
        /**
         * Allows user to modify their registration reason
         */
        async modifyReason() {
            await this.$store.dispatch('userUpdateRegistrationProcessRequest', {
                id: this.$store.getters.getProfile.id,
                data: { reason: '', partner: {} }
            })
            await this.$router.push({ name: 'register' })
        },
        
        /**
         * Builds the redirect URL for payment processor
         */
        buildRedirectUrl() {
            const baseUrl = `${process.env.VUE_APP_URL}/#/payment`
            
            // Determine the correct route suffix based on payment type
            let routeSuffix = ''
            if (this.isResubscription) {
                routeSuffix = '/resubscription'
            } else if (this.isSubscription) {
                routeSuffix = '/subscription'
            } else if (this.isDonationType) {
                routeSuffix = '/donation'
            }

            // Add any query parameters
            const queryParams = []
            if (this.isDonation && this.donation > 0) {
                queryParams.push(`donation=${this.donation}`)
            }

            // Construct the final URL
            const url = baseUrl + routeSuffix
            return queryParams.length ? `${url}?${queryParams.join('&')}` : url
        },
        
        /**
         * Handles form submission
         */
        async onSubmit() {
            if (!this.$refs.form.validate()) return
            
            this.loading = true
            try {
                const queries = {
                    user_id: this.$store.getters.getProfile.id,
                    redirect_url: this.buildRedirectUrl(),
                    subscription: (this.isSubscription || this.isResubscription) ? this.subscription : 0,
                    donation: this.isDonation ? this.donation : 0,
                    discounted: this.discounted
                }

                if (this.total > 0) {
                    await this.eTransactionRequest(queries)
                } else {
                    await this.processFreeSubscription()
                }
            } catch (error) {
                this.loading = false
                this.$store.dispatch('setErrors', error.response?.data || error.message)
            }
        },
        
        /**
         * Processes a free subscription based on user type
         */
        async processFreeSubscription() {
            if (this.isAja) {
                await this.ajaAdhRequest()
            } else {
                await this.freeAdhRequest()
            }
        },
        
        /**
         * Processes a free AJA membership request
         */
        ajaAdhRequest() {
            const queries = {
                user_id: this.$store.getters.getProfile.id,
                adh_secret_code: this.registration_process.partner.adh_secret_code,
                reason: this.registration_process.reason,
                aja_role: this.registration_process.aja_role,
                ref: 'ONCOMAN' + Date.now().toString(),
                note: `Adhesion gracieuse offerte dans le cadre du projet AJA`
            }
            
            this.$store.dispatch('ajaAdhesionRequest', queries)
                .then(async () => {
                    this.$store.commit('setSnack', { 'type': 'success', 'msg': 'Adhésion enregistrée !' })
                    
                    // Update user data
                    const userQueries = {
                        id: this.$store.getters.getProfile.id,
                        datas: {
                            ...this.$store.getters.getProfile,
                            followed_cancerous_pathology: this.followed_cancerous_pathology,
                            platform_access_terms_accepted: this.allPlatformTermsAccepted
                        }
                    }
                    
                    await this.$store.dispatch('userEditRequest', userQueries)
                    await this.$store.dispatch('profileRequest')
                    await this.$router.push({ name: 'aja-home' })
                    this.loading = false
                })
                .catch((err) => {
                    if (err.response?.status !== 403) {
                        this.loading = false
                    }
                    this.$store.dispatch('setErrors', err.response?.data)
                })
        },
        
        /**
         * Processes a free partner-provided membership
         */
        async freeAdhRequest() {
            const partnerId = this.registration_process.partner.id
            const partnerName = this.partners.find(p => parseInt(p.id) === parseInt(partnerId))?.name || 'partenaire'
            
            const queries = {
                user_id: this.$store.getters.getProfile.id,
                partner_id: partnerId,
                adh_secret_code: this.registration_process.partner.adh_secret_code,
                reason: this.registration_process.reason,
                ref: 'ONCOMAN' + Date.now().toString(),
                note: `Adhesion gracieuse offerte par ${partnerName}`
            }
            
            try {
                await this.$store.dispatch('freeAdhesionRequest', queries)
                this.$store.commit('setSnack', { 'type': 'success', 'msg': 'Adhésion enregistrée !' })
                
                // Update user data
                const userQueries = {
                    id: this.$store.getters.getProfile.id,
                    datas: {
                        ...this.$store.getters.getProfile,
                        followed_cancerous_pathology: this.followed_cancerous_pathology,
                        platform_access_terms_accepted: this.allPlatformTermsAccepted
                    }
                }
                
                await this.$store.dispatch('userEditRequest', userQueries)
                await this.$store.dispatch('profileRequest')
                await this.$router.push({ name: 'home' })
                
            } catch (err) {
                if (err.response?.status !== 403) {
                    this.$store.dispatch('setErrors', err.response?.data)
                }
            } finally {
                this.loading = false
            }
        },
        
        /**
         * Initiates e-transaction payment request
         */
        eTransactionRequest(queries) {
            this.$store.dispatch('etransactionsCreateRequest', queries)
                .then(async (eTransactions) => {
                    // Update user data
                    const userQueries = {
                        id: this.$store.getters.getProfile.id,
                        datas: {
                            ...this.$store.getters.getProfile,
                            followed_cancerous_pathology: this.followed_cancerous_pathology,
                            platform_access_terms_accepted: this.allPlatformTermsAccepted
                        }
                    }
                    
                    await this.$store.dispatch('userEditRequest', userQueries)
                    this.eTransactions = eTransactions
                    
                    // Submit form to payment processor
                    setTimeout(() => this.$refs.etransactionsForm.submit(), 0)
                })
                .catch((err) => {
                    this.loading = false
                    if (err.response?.status !== 403) {
                        this.$store.dispatch('setErrors', err.response?.data)
                    }
                })
        },

        // ========== PAYMENT RESPONSE HANDLING ==========
        /**
         * Handles the payment response from e-transactions
         */
        async handlePaymentResponse() {
            this.isConfirmation = true
            
            if (this.$route.query.code === '00000') {
                // Payment success
                await this.$store.dispatch('profileRequest')
                
                this.$store.commit('setSnack', { 'type': 'success', 'msg': 'Paiement effectué !' })
                this.showPaymentCard = false
                
                if (this.isSubscription || this.isResubscription) {
                    this.showBravoCard = true
                } else {
                    this.showThanksCard = true
                }
            } else {
                // Payment error
                await this.$store.dispatch('setErrors', { 
                    'message': 'Erreur lors du paiement veuillez recharger la page' 
                })
                
                if (this.$route.query.code === '00001') {
                    await this.$router.push({ 
                        ...this.$router.currentRoute, 
                        query: { subscription: this.$route.query.subscription }
                    })
                }
            }
        },
        
        /**
         * Retries a failed payment
         */
        retryPayment() {
            // Remove the code parameter and refresh the page
            const query = { ...this.$route.query }
            delete query.code
            
            this.$router.replace({ query })
                .catch(err => {
                    if (err.name !== 'NavigationDuplicated') {
                        throw err
                    }
                })
                .finally(() => {
                    window.location.reload()
                })
        }
    }
}
</script>
